import React, { useEffect, useRef, useState } from 'react';
import { App as AntApp, Button, Modal, Form, Input, Tag, message, Card, Select, Pagination, Radio, Typography, Tooltip, Collapse } from 'antd';
import { PlusOutlined, ToolOutlined, EyeOutlined, ExportOutlined, ApiOutlined, CopyOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { applicationsApi, dictionariesApi } from '../services/api-services';
import { resolveUrl } from '../utils/url';
import { useTheme } from '../contexts/ThemeContext';
import ImagePicker from '../components/ImagePicker';

const { TextArea } = Input;

const labelWithTip = (label: string, tip: React.ReactNode) => (
  <span>
    {label}
    <Tooltip title={tip}>
      <QuestionCircleOutlined style={{ marginLeft: 4, color: 'var(--apple-text-secondary, #999)', cursor: 'help' }} />
    </Tooltip>
  </span>
);

const ApplicationList: React.FC = () => {
  const { modal } = AntApp.useApp();
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null);
  const [sdkModalApp, setSdkModalApp] = useState<any>(null);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [appCategoryOptions, setAppCategoryOptions] = useState<{ label: string; value: string }[]>([]);
  const [activeCategory, setActiveCategory] = useState<string>('');

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'super_admin' || user.role === 'admin';

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const filteredData = activeCategory ? data.filter((item: any) => item.tags?.includes(activeCategory)) : data;
  const pagedData = filteredData.slice((page - 1) * pageSize, page * pageSize);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res: any = await applicationsApi.list();
      setData(res);
    } catch (e) {
      message.error('获取应用列表失败');
    }
    setLoading(false);
  };

  const fetchCategories = async () => {
    try {
      const dict: any = await dictionariesApi.getByCode('app_category');
      if (dict?.items) setAppCategoryOptions(dict.items.map((i: any) => ({ label: i.name, value: i.value })));
    } catch { /* use free input fallback */ }
  };

  useEffect(() => {
    fetchData(); fetchCategories();
    const onUpdated = () => fetchData();
    window.addEventListener('applications-updated', onUpdated);
    return () => window.removeEventListener('applications-updated', onUpdated);
  }, []);

  const handleSave = async (values: any) => {
    try {
      await applicationsApi.create(values);
      message.success('创建成功');
      setModalVisible(false);
      form.resetFields();
      setSlugAvailable(null);
      fetchData();
    } catch (e: any) {
      message.error(e.response?.data?.detail || '操作失败');
    }
  };

  const openCreateModal = () => {
    form.resetFields();
    form.setFieldsValue({ app_type: 'subapp' });
    setSlugAvailable(null);
    setModalVisible(true);
  };

  return (
    <div className="apple-page">
      <div className="apple-page-header">
        <h1 className="apple-page-title">应用管理</h1>
        <Button className="apple-btn-create" icon={<PlusOutlined />} onClick={openCreateModal}>新建应用</Button>
      </div>
      <p className="apple-page-subtitle">管理和配置您的应用程序，包括预设和自定义应用。</p>

      {appCategoryOptions.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
          <Tag
            style={{ cursor: 'pointer', padding: '4px 12px', fontSize: 13 }}
            color={activeCategory === '' ? 'blue' : undefined}
            onClick={() => { setActiveCategory(''); setPage(1); }}
          >全部</Tag>
          {appCategoryOptions.map((opt) => (
            <Tag
              key={opt.value}
              style={{ cursor: 'pointer', padding: '4px 12px', fontSize: 13 }}
              color={activeCategory === opt.value ? 'blue' : undefined}
              onClick={() => { setActiveCategory(activeCategory === opt.value ? '' : opt.value); setPage(1); }}
            >{opt.label}</Tag>
          ))}
        </div>
      )}

      <div className="app-card-grid">
        {pagedData.map((item: any) => (
          <div key={item.id}>
            <Card hoverable className="app-card" style={{ position: 'relative' }}>
              {isAdmin && (
                <Button
                  type="text"
                  size="small"
                  icon={<ToolOutlined />}
                  className="app-card-edit-btn"
                  onClick={(e) => { e.stopPropagation(); navigate(`/app/${item.slug || item.id}/info`); }}
                />
              )}
              <div className="app-card-header">
                <div className="app-card-icon-wrap">
                  {item.icon && (item.icon.startsWith('http') || item.icon.startsWith('data:') || item.icon.startsWith('/uploads/') || item.icon.startsWith('/avatars/') || item.icon.startsWith('/app-icons/')) ? (
                    <img src={resolveUrl(item.icon)} alt={item.name} className="app-card-icon" />
                  ) : item.icon ? (
                    <span className="app-card-icon-emoji">{item.icon}</span>
                  ) : (
                    <span className="app-card-icon-fallback">{item.name?.charAt(0)?.toUpperCase()}</span>
                  )}
                </div>
                <div className="app-card-header-info">
                  <span className="app-card-name">{item.name}</span>
                  <div className="app-card-tags">
                    {item.app_type === 'backend' && <Tag color="purple">后端应用</Tag>}
                    {item.tags && item.tags.length > 0
                      ? item.tags.map((tag: string, i: number) => <Tag key={i}>{tag}</Tag>)
                      : (item.app_type !== 'backend' && <span style={{ fontSize: 12, color: 'var(--apple-text-secondary)' }}>暂无标签</span>)}
                  </div>
                </div>
              </div>
              <div className="app-card-body">
                <p className="app-card-desc" style={{ 
                  overflow: 'hidden', 
                  textOverflow: 'ellipsis', 
                  display: '-webkit-box', 
                  WebkitLineClamp: 2, 
                  WebkitBoxOrient: 'vertical' 
                }}>{item.description || '暂无描述'}</p>
              </div>
              <div className="app-card-footer">
                {item.app_type === 'backend' ? (
                  <Button type="link" size="small" icon={<ApiOutlined />} style={{ marginLeft: 'auto' }} onClick={() => setSdkModalApp(item)}>
                    对接说明
                  </Button>
                ) : (
                  <>
                    <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => {
                      if (!item.has_static) {
                        modal.confirm({
                          title: '未上传插件',
                          content: '该应用尚未上传源文件，请前往上传。',
                          okText: '前往上传',
                          cancelText: '取消',
                          onOk: () => navigate(`/app/${item.slug || item.id}/info`, { state: { activeTab: 'static' } }),
                        });
                      } else {
                        navigate(`/app/${item.slug || item.id}/view`);
                      }
                    }}>进入</Button>
                    <Button type="link" size="small" icon={<ExportOutlined />} onClick={() => {
                      if (!item.has_static) {
                        modal.confirm({
                          title: '未上传插件',
                          content: '该应用尚未上传源文件，请前往上传。',
                          okText: '前往上传',
                          cancelText: '取消',
                          onOk: () => navigate(`/app/${item.slug || item.id}/info`, { state: { activeTab: 'static' } }),
                        });
                      } else {
                        const standaloneId = item.slug || item.id;
                        const standaloneUrl = `/app/${standaloneId}/index?theme=${theme}`;
                        if ((window as any).__TAURI__) {
                          import('@tauri-apps/api/webviewWindow').then(({ WebviewWindow }) => {
                            new WebviewWindow(`standalone-${item.id}`, {
                              url: `index.html?__route=${encodeURIComponent(standaloneUrl)}`,
                              title: item.name || '应用', width: 1200, height: 800,
                            });
                          });
                        } else {
                          window.open(standaloneUrl, '_blank');
                        }
                      }
                    }}>独立访问</Button>
                  </>
                )}
              </div>
            </Card>
          </div>
        ))}
      </div>

      {filteredData.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
          <Pagination current={page} pageSize={pageSize} total={filteredData.length} onChange={(p) => setPage(p)} showSizeChanger pageSizeOptions={['10', '20', '50', '100']} onShowSizeChange={(_, size) => { setPageSize(size); setPage(1); }} showTotal={(t) => `共 ${t} 条`} />
        </div>
      )}

      {filteredData.length === 0 && !loading && (
        <div className="apple-empty">
          <p className="apple-empty-title">暂无应用</p>
          <p className="apple-empty-desc">点击"新建应用"开始创建</p>
        </div>
      )}

      <Modal
        className="apple-modal"
        title="新建应用"
        open={modalVisible}
        onCancel={() => { setModalVisible(false); form.resetFields(); setSlugAvailable(null); }}
        onOk={() => form.submit()}
        width={700}
      >
        <Form className="apple-form" form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item name="name" label="名称" rules={[{ required: true, message: '请输入应用名称' }]}>
            <Input placeholder="请输入应用名称" />
          </Form.Item>
          <Form.Item
            name="app_type"
            label={labelWithTip('应用类型', '子应用：带管理后台，可在平台内直接访问；后端应用：仅提供 sdk.js 供第三方平台对接，不在平台内独立运行。')}
            initialValue="subapp"
          >
            <Radio.Group>
              <Radio.Button value="subapp">子应用</Radio.Button>
              <Radio.Button value="backend">后端应用</Radio.Button>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            name="slug"
            label={labelWithTip('应用标识', '唯一英文标识，留空则自动从名称生成，创建后不可修改')}
            extra={slugAvailable === true ? <span style={{ color: '#52c41a' }}>✓ 该标识可用</span> : undefined}
            validateTrigger="onBlur"
            rules={[
              { pattern: /^[a-zA-Z0-9_]+$/, message: '仅限字母、数字、下划线' },
              {
                validator: async (_, value) => {
                  if (!value) { setSlugAvailable(null); return; }
                  try {
                    await applicationsApi.getBySlug(value);
                    setSlugAvailable(false);
                    return Promise.reject('该标识已被使用');
                  } catch {
                    setSlugAvailable(true);
                  }
                },
              },
            ]}
          >
            <Input placeholder="如：my_app（仅限字母、数字、下划线）" onChange={() => setSlugAvailable(null)} />
          </Form.Item>
          <Form.Item name="icon" label="图标">
            <ImagePicker shape="square" />
          </Form.Item>
          <Form.Item name="tags" label="分类标签">
            <Select
              mode="multiple"
              placeholder="选择或输入分类标签"
              options={appCategoryOptions}
              allowClear
            />
          </Form.Item>
          <Form.Item name="description" label="描述">
            <TextArea rows={3} placeholder="请输入应用描述" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        className="apple-modal"
        title={`对接说明 - ${sdkModalApp?.name || ''}`}
        open={!!sdkModalApp}
        onCancel={() => setSdkModalApp(null)}
        footer={<Button onClick={() => setSdkModalApp(null)}>关闭</Button>}
        width={960}
      >
        {sdkModalApp && (() => {
          const appId = sdkModalApp.id;
          const origin = window.location.origin;
          const sdkUrl = `${origin}/api/v1/sdk/sdk.js`;
          const sdkSnippet = `<!-- 1. 引入平台 SDK -->\n`
            + `<script src="${sdkUrl}"></script>\n`
            + `<script>\n`
            + `  // 2. 初始化\n`
            + `  UzySDK.init({\n`
            + `    apiBase: '${origin}',\n`
            + `    appId: ${appId}\n`
            + `  });\n`
            + `  // 3. 设置 token\n`
            + `  UzySDK.setToken('<第三方token>');\n`
            + `\n`
            + `  // 4. API 调用\n`
            + `  UzySDK.callAction('your_action', { /* payload */ }, function (res) {\n`
            + `    console.log(res);\n`
            + `  });\n`
            + `\n`
            + `  // 5. 流式调用\n`
            + `  UzySDK.callAgent('your_action', { /* payload */ },\n`
            + `    function (chunk) { /* 逐段输出 */ },\n`
            + `    function (finalText) { /* 完成 */ }\n`
            + `  );\n`
            + `</script>`;
          const copy = (text: string) => {
            navigator.clipboard?.writeText(text).then(
              () => message.success('已复制'),
              () => message.error('复制失败'),
            );
          };
          const codeBox = (text: string) => (
            <div style={{ position: 'relative' }}>
              <Button
                size="small"
                icon={<CopyOutlined />}
                style={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
                onClick={() => copy(text)}
              >复制</Button>
              <pre style={{
                background: 'var(--apple-bg-secondary, #f5f5f7)',
                padding: 16, borderRadius: 8, overflow: 'auto', fontSize: 12, margin: 0,
              }}>{text}</pre>
            </div>
          );
          return (
            <div>
              <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
                第三方平台引入 sdk.js，调用 <code>init</code> 初始化并用 <code>setToken</code> 设置自己的 token 后，
                即可通过 <code>callAction</code> / <code>callAgent</code> 调用本应用的方法（即 <code>server.py</code> 中 <code>handlers</code> 声明的方法）。
                平台会将 token 转发到你在「应用详情 → 第三方对接」配置的回调地址校验，通过后才执行。
              </Typography.Paragraph>

              <Typography.Title level={5}>使用步骤</Typography.Title>
              <ol style={{ paddingLeft: 20, marginBottom: 16, color: 'var(--apple-text-secondary)' }}>
                <li>在「应用详情 → 第三方对接」中配置回调地址（callback_url）。</li>
                <li>第三方页面引入 sdk.js，调用 <code>UzySDK.init({'{ apiBase, appId }'})</code> 完成初始化（跨域必填），并用 <code>UzySDK.setToken()</code> 设置 token。</li>
                <li>调用 <code>UzySDK.callAction</code>（API 调用）或 <code>UzySDK.callAgent</code>（流式）。</li>
                <li>平台 POST <code>{'{ token, appId, action }'}</code> 到回调地址，返回 HTTP 200（且 body 非 <code>{'{ valid: false }'}</code>）即放行。</li>
              </ol>

              <Typography.Title level={5} style={{ marginTop: 16 }}>常用方法</Typography.Title>
              <ul style={{ paddingLeft: 20, color: 'var(--apple-text-secondary)' }}>
                <li><code>UzySDK.init({'{ apiBase, appId }'})</code> —— 初始化。</li>
                <li><code>UzySDK.setToken(token)</code> —— 设置 / 刷新鉴权 token（传空串可清除）。</li>
                <li><code>UzySDK.callAction(action, payload, cb)</code> —— API 调用，回调返回结果。</li>
                <li><code>UzySDK.callAgent(action, payload, onChunk, onDone)</code> —— 流式调用，逐段输出。</li>
              </ul>

              <Collapse
                style={{ marginTop: 16 }}
                items={[{ key: 'sdk', label: 'SDK 接入示例', children: codeBox(sdkSnippet) }]}
              />
            </div>
          );
        })()}
      </Modal>
    </div>
  );
};

export default ApplicationList;
